// Render-only TASK conversation: the complete oldest-first shared thread, its live preview, current plan, and
// the task-scoped composer. All orchestration lives in `useTaskThread`; this component renders only projection
// data and wires interactions. It reuses the global chat's activity, plan, markdown, bubble, and caret vocabulary
// without importing global Session creation/history/resume behavior.
//
// FEED CHRONOLOGY (v2 Signal): a day divider opens each new calendar day the thread spans, and run markers
// carry compact 24h times — BOTH are pure display derivations of the run's own recorded stamps (G-182; the
// prototype's dormant .m-day style, wired the same way the modal's .m-toggle was). Only the wire's canonical
// ISO stamp is reformatted; any other carried value renders verbatim (same rule as the card's wake chip).

import { Fragment, useEffect, useLayoutEffect, useRef } from 'react';
import { Icon } from './Icon.tsx';
import { ChatPlan } from './ChatPlan.tsx';
import { InboundEmailCard, ReferencedEmailCard } from './ChatEmailCard.tsx';
import { OrderedChatParts } from './OrderedChatParts.tsx';
import { ThinkingOrb } from './ThinkingOrb.tsx';
import { boundRetainedDraft } from './draft-text-limit.ts';
import type { ChatActivityRow, ChatTranscriptTurn } from '../surfaces/projections.ts';
import { liveStreamParts } from '../surfaces/chat-turns.ts';
import type { ChatTurnPart } from '../surfaces/chat-turns.ts';
import type { TaskDetailProjection, TaskThreadRun } from '../surfaces/know-projections.ts';
import type { TaskThreadController } from '../surfaces/useTaskThread.ts';
import { parseInboundEmailTurn } from '../surfaces/chat-email.ts';

/** Legacy runs predate durable parts, so retain their established activity-before-answer fallback. */
function legacyAgentParts(
  turn: ChatTranscriptTurn,
  activity: readonly ChatActivityRow[],
): readonly ChatTurnPart[] {
  return [
    ...activity.map((row): ChatTurnPart => ({ type: 'activity', activity: row })),
    ...(turn.text.length === 0 ? [] : [{ type: 'text' as const, text: turn.text }]),
  ];
}

/** True only for the wire's canonical ISO-8601 stamp; anything else stays a verbatim display value. */
function isIsoStamp(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T/.test(value) && !Number.isNaN(new Date(value).getTime());
}

/** Compact 24h time for an ISO stamp; a non-ISO value renders exactly as carried. */
function shortTime(value: string): string {
  if (!isIsoStamp(value)) return value;
  return new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** The day-divider label for a run's start stamp ("Friday, 26 June 2026"); null when underivable. */
function runDayLabel(value: string): string | null {
  if (!isIsoStamp(value)) return null;
  return new Date(value).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Plain words for a run's recorded state ("Run running" read as a stutter). Display-only mapping. */
const RUN_STATE_LABEL: Readonly<Record<TaskThreadRun['state'], string>> = {
  running: 'Run in progress',
  completed: 'Run finished',
  failed: 'Run failed',
  interrupted: 'Run stopped',
};

function RunMarker({
  run,
  thread,
  canContinue,
}: {
  readonly run: TaskThreadRun;
  readonly thread: TaskThreadController;
  readonly canContinue: boolean;
}): React.JSX.Element {
  const failed = run.state === 'failed' || run.state === 'interrupted';
  const paused = run.state === 'interrupted' && run.interruptedBy === 'inactivity';
  return (
    <div
      className="m-ev"
      data-run-state={run.state}
      {...(paused ? { 'data-run-paused': true } : {})}
    >
      <span className="eico" aria-hidden="true">
        <Icon name={failed ? 'x' : run.state === 'running' ? 'clock' : 'check'} size={13} />
      </span>
      <div className="ebody">
        <div className="et">{paused ? 'Run paused — no progress' : RUN_STATE_LABEL[run.state]}</div>
        <div className="emono">
          {shortTime(run.startedAt)}
          {run.endedAt !== null
            ? ` → ${shortTime(run.endedAt)}`
            : run.state === 'running'
              ? ' →'
              : ''}
        </div>
      </div>
      {paused && canContinue ? (
        <button
          type="button"
          className="btn quiet sm"
          disabled={thread.continuingRunId !== null}
          onClick={() => void thread.continueRun(run.id)}
        >
          Continue
        </button>
      ) : null}
    </div>
  );
}

function HumanTurn({
  turn,
  name,
}: {
  readonly turn: ChatTranscriptTurn;
  readonly name: string | null;
}) {
  if (turn.sourceRef?.type === 'comms_message') {
    return (
      <div className="task-turn chat-email-turn" data-turn={turn.id} data-author="human">
        <ReferencedEmailCard messageId={turn.sourceRef.messageId} />
      </div>
    );
  }
  const legacyEmail = turn.legacyInboundMessage === true ? parseInboundEmailTurn(turn.text) : null;
  if (legacyEmail !== null) {
    return (
      <div className="task-turn chat-email-turn" data-turn={turn.id} data-author="human">
        <InboundEmailCard email={legacyEmail} />
      </div>
    );
  }
  return (
    <div className="bubble me task-turn" data-turn={turn.id} data-author="human">
      {name !== null ? <span className="task-turn-author">{name}</span> : null}
      <span className="chat-plain">{turn.text}</span>
    </div>
  );
}

function AgentTurn({
  turn,
  activity,
  parts,
}: {
  readonly turn: ChatTranscriptTurn;
  readonly activity: readonly ChatActivityRow[];
  readonly parts?: readonly ChatTurnPart[];
}): React.JSX.Element {
  return (
    <div className="bubble them task-turn" data-turn={turn.id} data-author="agent">
      <OrderedChatParts parts={parts ?? legacyAgentParts(turn, activity)} />
    </div>
  );
}

/** A Scheduler-authored wake is visible, but never impersonates a teammate or the Agent's answer. */
function PlatformTurn({ turn }: { readonly turn: ChatTranscriptTurn }): React.JSX.Element {
  return (
    <div className="bubble task-turn task-platform-turn" data-turn={turn.id} data-author="platform">
      <span className="task-turn-author">Ritona platform</span>
      <span className="chat-plain">{turn.text}</span>
    </div>
  );
}

/** The live PREVIEW turn — the parts as they arrive. The run's thinking affordance used to sit under it in
 *  the feed; it now takes the composer's own footprint below (owner, 11 Aug 2026), so a working run puts no
 *  extra box in the thread. Nothing renders here until the run has actually said something. */
function LivePreview({
  preview,
}: {
  readonly preview: TaskThreadController['preview'];
}): React.JSX.Element | null {
  const parts = liveStreamParts(preview);
  if (parts.length === 0) return null;
  return (
    <div className="bubble them task-turn" data-streaming-turn>
      <OrderedChatParts parts={parts} streaming />
    </div>
  );
}

/** The task thread's thinking affordance, relocated WHOLE out of the feed and into the composer row — now
 *  carrying Stop where the dock carries it (owner, 11 Aug 2026). Stop appears only once this task has a run
 *  to stop: during the beat between submitting and the run existing there is nothing to interrupt. */
function TaskThinkingRow({ thread }: { readonly thread: TaskThreadController }): React.JSX.Element {
  return (
    <div role="status" data-testid="task-thinking" className="chat-thinking">
      <ThinkingOrb />
      <span>Ritona is thinking…</span>
      {thread.activeRunId !== null ? (
        <button
          type="button"
          onClick={() => void thread.interrupt()}
          className="btn quiet sm chat-thinking-stop"
          aria-label="Stop Ritona on this task"
        >
          Stop
        </button>
      ) : null}
    </div>
  );
}

/** One persisted run. Activity appears once beside its first agent turn, or alone if no answer was recorded. */
function TaskRun({
  run,
  preview,
  thread,
  canContinue,
}: {
  readonly run: TaskThreadRun;
  readonly preview: TaskThreadController['preview'] | null;
  readonly thread: TaskThreadController;
  readonly canContinue: boolean;
}): React.JSX.Element {
  const firstAgent = run.turns.findIndex((turn) => turn.author === 'agent');
  return (
    <section className="task-thread-run" data-run={run.id}>
      <RunMarker run={run} thread={thread} canContinue={canContinue} />
      {run.turns.map((turn, index) => {
        if (turn.author === 'human') {
          return <HumanTurn key={turn.id} turn={turn} name={run.initiatedByDisplayName} />;
        }
        if (turn.author === 'platform') return <PlatformTurn key={turn.id} turn={turn} />;
        return (
          <AgentTurn
            key={turn.id}
            turn={turn}
            activity={index === firstAgent ? run.activity : []}
            {...(index === firstAgent && run.parts !== undefined ? { parts: run.parts } : {})}
          />
        );
      })}
      {preview !== null && firstAgent === -1 ? (
        <LivePreview preview={preview} />
      ) : firstAgent === -1 && (run.parts?.length ?? run.activity.length) > 0 ? (
        <div className="bubble them task-turn" data-run-activity-only>
          <OrderedChatParts
            parts={
              run.parts ?? run.activity.map((activity) => ({ type: 'activity', activity }) as const)
            }
          />
        </div>
      ) : null}
    </section>
  );
}

/** Pair each run with the day divider it opens: the first run of each NEW calendar day carries the day's
 *  label; a run whose stamp cannot derive a day joins the current day silently (never a blank divider). */
function withDayDividers(
  runs: readonly TaskThreadRun[],
): readonly { readonly run: TaskThreadRun; readonly divider: string | null }[] {
  let currentDay: string | null = null;
  return runs.map((run) => {
    const label = runDayLabel(run.startedAt);
    const divider = label !== null && label !== currentDay ? label : null;
    if (label !== null) currentDay = label;
    return { run, divider };
  });
}

/** How close to the end (in CSS pixels) still counts as "reading the latest" for the follow behaviour. */
const FOLLOW_SLACK = 48;

/** Chat-style follow for the task feed: it opens on the latest rows and keeps the newest row in view as the
 *  run streams, unless the reader has scrolled up to read history (then it leaves them where they are).
 *  Display-only — it moves the scroll position and nothing else. */
function useFollowLatest(): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null);
  const following = useRef(true);
  useEffect(() => {
    const feed = ref.current;
    if (feed === null) return undefined;
    const onScroll = (): void => {
      following.current = feed.scrollHeight - feed.scrollTop - feed.clientHeight <= FOLLOW_SLACK;
    };
    feed.addEventListener('scroll', onScroll, { passive: true });
    return () => feed.removeEventListener('scroll', onScroll);
  }, []);
  useLayoutEffect(() => {
    const feed = ref.current;
    if (feed !== null && following.current) feed.scrollTop = feed.scrollHeight;
  });
  return ref;
}

function TaskThreadFeed({
  runs,
  thread,
}: {
  readonly runs: readonly TaskThreadRun[];
  readonly thread: TaskThreadController;
}): React.JSX.Element {
  const { preview } = thread;
  const feedRef = useFollowLatest();
  const previewInSnapshot =
    preview.runId !== null && runs.some((candidate) => candidate.id === preview.runId);
  return (
    <div
      ref={feedRef}
      className="m-feed"
      data-task-thread
      role="log"
      aria-label="Task conversation"
    >
      {runs.length === 0 && preview.runId === null ? (
        <div className="m-note">
          No updates yet — this shared task thread starts with the first action.
        </div>
      ) : null}
      {withDayDividers(runs).map(({ run, divider }) => (
        <Fragment key={run.id}>
          {divider !== null ? (
            <div className="m-day" data-thread-day={divider}>
              <span>{divider}</span>
            </div>
          ) : null}
          <TaskRun
            run={run}
            preview={preview.runId === run.id ? preview : null}
            thread={thread}
            canContinue={run.id === runs.at(-1)?.id}
          />
        </Fragment>
      ))}
      {!previewInSnapshot && preview.runId !== null ? (
        <section className="task-thread-run" data-run={preview.runId} data-run-state="running">
          <LivePreview preview={preview} />
        </section>
      ) : null}
    </div>
  );
}

function disabledMessage(detail: TaskDetailProjection): string {
  return detail.state === 'done' || detail.state === 'dropped'
    ? 'This task is closed. Its shared thread remains on the record.'
    : 'Messaging is not available to you on this task.';
}

/** Latest Agent working plan for the Task Session; distinct from the human-approved definition rail. */
function latestWorkingPlan(detail: TaskDetailProjection): TaskThreadRun['plan'] {
  for (let index = detail.runs.length - 1; index >= 0; index -= 1) {
    const plan = detail.runs[index]?.plan;
    if (plan !== null && plan !== undefined) return plan;
  }
  return null;
}

/** Why a send was refused or queued, or why this thread is closed to the reader. It sits ABOVE the composer
 *  region in BOTH its states, so a queued admission stays readable while the run it is waiting on works. */
function TaskComposerNote({
  detail,
  thread,
}: {
  readonly detail: TaskDetailProjection;
  readonly thread: TaskThreadController;
}): React.JSX.Element | null {
  if (thread.note === null && thread.canMessage) return null;
  return (
    <p role="status" className="gate-note task-thread-note">
      {thread.note ?? disabledMessage(detail)}
    </p>
  );
}

/** The IDLE state of the task composer: the input + Send. The `sending` guard that used to disable Send
 *  lives in the STATE now — this form is off the screen entirely while work is in flight. */
function TaskComposerForm({
  detail,
  thread,
}: {
  readonly detail: TaskDetailProjection;
  readonly thread: TaskThreadController;
}): React.JSX.Element {
  return (
    <form
      aria-label="Message Ritona on this task"
      className="m-inrow"
      onSubmit={(event) => {
        event.preventDefault();
        void thread.send();
      }}
    >
      <div className="box">
        <input
          type="text"
          value={thread.draft}
          onChange={(event) => thread.setDraft(boundRetainedDraft(event.target.value))}
          disabled={!thread.canMessage}
          aria-label={`Message Ritona on this task: ${detail.title}`}
          placeholder="Message Ritona on this task…"
        />
      </div>
      <button
        type="submit"
        className="btn sm"
        disabled={!thread.canMessage || thread.draft.trim().length === 0}
        aria-label="Send message on this task"
      >
        Send
      </button>
    </form>
  );
}

/**
 * The task composer REGION — the same two-state control the general dock now uses (owner, 11 Aug 2026):
 * idle it is the input + Send; while this task's work is in flight the SAME row carries the thinking
 * affordance instead of stacking it in the feed above. `thread.sending` is the swap condition because it is
 * already the union the composer was disabled by — a send in flight, a live run, or a queued admission — so
 * the row now SHOWS the reason it was dead rather than presenting an input that does nothing.
 *
 * It carries Stop in the same seat as the dock's (owner, 11 Aug 2026). No new control machinery was minted
 * for it: the action is the same human-only run interrupt the dock calls, so "who may stop a run" keeps its
 * single answer, decided in the agent module.
 */
function TaskComposer({
  detail,
  thread,
}: {
  readonly detail: TaskDetailProjection;
  readonly thread: TaskThreadController;
}): React.JSX.Element {
  return (
    <>
      <TaskComposerNote detail={detail} thread={thread} />
      {thread.sending ? (
        <div className="m-inrow m-inrow-run">
          <TaskThinkingRow thread={thread} />
        </div>
      ) : (
        <TaskComposerForm detail={detail} thread={thread} />
      )}
    </>
  );
}

/** Complete shared Task conversation: persisted snapshot + ephemeral preview + scoped composer. */
export function TaskThreadConversation({
  detail,
  thread,
}: {
  readonly detail: TaskDetailProjection;
  readonly thread: TaskThreadController;
}): React.JSX.Element {
  return (
    <div className="m-conv">
      <TaskThreadFeed runs={detail.runs} thread={thread} />
      <ChatPlan plan={thread.preview.plan ?? latestWorkingPlan(detail)} />
      <TaskComposer detail={detail} thread={thread} />
    </div>
  );
}
